import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, Clock } from "lucide-react";
import { apiError } from "@/lib/apiError";

export const PriceRefreshBar = ({ onDone }) => {
  const { getAuthHeaders } = useAuth();
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = () =>
    axios.get(`${API}/admin/prices/last-run`, { headers: getAuthHeaders() }).then((r) => {
      setRun(r.data.run);
      return r.data.run;
    });

  useEffect(() => {
    load().catch(() => {});
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/prices/refresh-all`, {}, { headers: getAuthHeaders() });
      toast.success(r.data.message);
      const startedAt = Date.now();
      pollRef.current = setInterval(async () => {
        const latest = await load().catch(() => null);
        const timedOut = Date.now() - startedAt > 10 * 60 * 1000;
        if (latest?.status === "done" || latest?.status === "failed" || timedOut) {
          clearInterval(pollRef.current);
          setBusy(false);
          if (latest?.status === "done") {
            toast.success(`Prices refreshed: ${latest.prices_updated} updated across ${latest.products} products`);
            onDone?.();
          } else {
            toast.error("Price refresh didn't finish. Please try again later.");
          }
        }
      }, 4000);
    } catch (err) {
      toast.error(apiError(err, "Couldn't start price refresh"));
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded-2xl bg-primary-50 border border-primary-100" data-testid="price-refresh-bar">
      <Clock className="w-4 h-4 text-primary-500" />
      <p className="text-sm text-primary-700 flex-1 min-w-[200px]" data-testid="price-refresh-last-run">
        Comparison prices auto-refresh daily at 6:00 AM IST.
        {run && <> Last run: {new Date(run.started_at).toLocaleString("en-IN")} ({run.status}{run.status === "done" ? `, ${run.prices_updated} updated` : ""}).</>}
      </p>
      <Button size="sm" variant="outline" onClick={start} disabled={busy} data-testid="price-refresh-all-btn">
        <RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} />{busy ? "Refreshing..." : "Refresh all now"}
      </Button>
    </div>
  );
};
